/**
 * Camada única de agregação — Cockpit de Execução Estratégica (Metas / Hoshin).
 * Deriva tudo de entidades existentes: objectives, goals, actions, projects, indicators, issues.
 */
import type { Indicator } from '../types/indicator';
import type { EnterpriseProject } from '../types/enterpriseProject';
import type { ObjectiveNode, TacticalGoalNode } from './hoshinService';
import { computeGoalStatus } from './hoshinService';
import type { ProjectHubIssue } from './projectService';

// ─── Health & probability ───────────────────────────────────────────────────

export type GoalHealth = 'healthy' | 'attention' | 'at_risk' | 'critical';

export const GOAL_HEALTH_LABELS: Record<GoalHealth, string> = {
  healthy: 'Saudável',
  attention: 'Atenção',
  at_risk: 'Em Risco',
  critical: 'Crítica',
};

export const GOAL_HEALTH_DOT: Record<GoalHealth, string> = {
  healthy: 'bg-emerald-500',
  attention: 'bg-amber-400',
  at_risk: 'bg-orange-500',
  critical: 'bg-red-600',
};

export const GOAL_HEALTH_BADGE: Record<GoalHealth, string> = {
  healthy: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  attention: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
  at_risk: 'bg-orange-500/10 text-orange-700 dark:text-orange-400',
  critical: 'bg-red-500/10 text-red-700 dark:text-red-400',
};

export interface LinkedAction {
  id: string;
  title: string;
  status: string;
  project_id: string | null;
  due_date: string | null;
  assigned_to: string | null;
  hoshin_goal_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface LinkedProjectSummary {
  id: string;
  name: string;
  status: string;
  progress: number;
  health: GoalHealth;
  roiPercent: number | null;
  economia: number;
  actionCount: number;
  openActions: number;
  overdueActions: number;
  problemCount: number;
  criticalProblems: number;
}

export interface BowlingMonth {
  label: string;
  monthKey: string;
  planned: number;
  actual: number | null;
  gap: number | null;
  tone: 'green' | 'amber' | 'red' | 'neutral';
  isPast: boolean;
  isCurrent: boolean;
}

export interface EnrichedGoalNode extends TacticalGoalNode {
  health: GoalHealth;
  plannedProgress: number;
  achievementProbability: number;
  probabilityLabel: string;
  overdueActions: number;
  openActions: number;
  criticalIssues: number;
  linkedProjects: LinkedProjectSummary[];
  linkedIndicators: Indicator[];
  linkedActions: LinkedAction[];
  linkedIssues: ProjectHubIssue[];
  bowling: BowlingMonth[];
  ownerName: string;
  departmentName: string;
}

export interface EnrichedObjectiveNode extends ObjectiveNode {
  health: GoalHealth;
  actualProgress: number;
  plannedProgress: number;
  achievementProbability: number;
  probabilityLabel: string;
  goals: EnrichedGoalNode[];
  linkedProjects: LinkedProjectSummary[];
  linkedIndicators: Indicator[];
  linkedActions: LinkedAction[];
  linkedIssues: ProjectHubIssue[];
  bowling: BowlingMonth[];
  sponsorName: string;
  overdueActions: number;
  criticalIssues: number;
}

export interface HoshinExecutiveSummary {
  totalObjectives: number;
  healthyGoals: number;
  attentionGoals: number;
  atRiskGoals: number;
  criticalGoals: number;
  averageProbability: number;
  linkedProjects: number;
  overdueActions: number;
  criticalProblems: number;
  totalActions: number;
}

export interface HoshinDashboardData {
  objectives: EnrichedObjectiveNode[];
  summary: HoshinExecutiveSummary;
  projectById: Map<string, EnterpriseProject>;
  indicatorById: Map<string, Indicator>;
}

export interface BuildHoshinDashboardInput {
  tree: ObjectiveNode[];
  actions: LinkedAction[];
  projects: EnterpriseProject[];
  indicators: Indicator[];
  issuesByProject: Map<string, ProjectHubIssue[]>;
  memberNameById: Map<string, string>;
  departmentNameById: Map<string, string>;
}

// ─── Math helpers ───────────────────────────────────────────────────────────

export function computePlannedProgress(createdAt: string, targetDate: string | null, now = new Date()): number {
  if (!targetDate) return 0;
  const target = new Date(targetDate);
  const created = new Date(createdAt);
  if (isNaN(target.getTime()) || isNaN(created.getTime())) return 0;
  const totalMs = target.getTime() - created.getTime();
  if (totalMs <= 0) return 100;
  const elapsedMs = now.getTime() - created.getTime();
  return Math.round(Math.min(100, Math.max(0, (elapsedMs / totalMs) * 100)));
}

export function computeGoalHealth(
  actualProgress: number,
  plannedProgress: number,
  tacticalStatus: ReturnType<typeof computeGoalStatus>,
  overdueActions: number,
  criticalIssues: number,
): GoalHealth {
  if (tacticalStatus === 'delayed' || criticalIssues > 0) return 'critical';
  if (actualProgress >= 100) return 'healthy';
  const gap = plannedProgress - actualProgress;
  if (gap <= 2) return 'healthy';
  if (gap <= 10) return 'attention';
  if (gap <= 20 || tacticalStatus === 'at_risk' || overdueActions >= 2) return 'at_risk';
  return 'critical';
}

export function computeAchievementProbability(input: {
  actualProgress: number;
  plannedProgress: number;
  completedActions: number;
  totalActions: number;
  overdueActions: number;
  criticalIssues: number;
  daysRemaining: number;
  createdAt: string;
  targetDate: string | null;
}): number {
  const {
    actualProgress,
    plannedProgress,
    completedActions,
    totalActions,
    overdueActions,
    criticalIssues,
    daysRemaining,
    createdAt,
    targetDate,
  } = input;

  let score = 55 + (actualProgress - plannedProgress) * 0.8;

  if (totalActions > 0) {
    const completionRate = completedActions / totalActions;
    score += completionRate * 25;
    score -= (overdueActions / totalActions) * 30;
  }

  if (daysRemaining > 0 && targetDate) {
    const created = new Date(createdAt).getTime();
    const target = new Date(targetDate).getTime();
    const elapsed = Date.now() - created;
    const total = target - created;
    if (elapsed > 0 && actualProgress > 0) {
      const velocity = actualProgress / (elapsed / 86400000);
      const projected = actualProgress + velocity * daysRemaining;
      score = score * 0.55 + projected * 0.45;
    }
  } else if (daysRemaining <= 0 && actualProgress < 100) {
    score = Math.min(score, 20);
  }

  score -= criticalIssues * 10;
  score -= overdueActions * 4;

  return Math.round(Math.max(3, Math.min(97, score)));
}

export function probabilityLabel(probability: number): string {
  if (probability >= 80) return 'Alta chance de sucesso';
  if (probability >= 60) return 'Atenção';
  if (probability >= 40) return 'Risco moderado';
  return 'Risco elevado';
}

function daysRemaining(targetDate: string | null): number {
  if (!targetDate) return 999;
  const diff = new Date(targetDate).getTime() - Date.now();
  return Math.ceil(diff / 86400000);
}

function isActionOverdue(action: LinkedAction): boolean {
  if (action.status === 'completed' || action.status === 'cancelled') return false;
  if (!action.due_date) return false;
  const due = new Date(action.due_date);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
}

function isIssueCritical(issue: ProjectHubIssue): boolean {
  const sev = issue.severity?.toLowerCase() ?? '';
  const st = issue.status?.toLowerCase() ?? '';
  if (st === 'resolved' || st === 'closed' || st === 'done') return false;
  return sev === 'critical' || sev === 'high' || sev === 'urgent';
}

function worstHealth(healths: GoalHealth[]): GoalHealth {
  const rank: Record<GoalHealth, number> = { healthy: 0, attention: 1, at_risk: 2, critical: 3 };
  return healths.reduce((worst, h) => (rank[h] > rank[worst] ? h : worst), 'healthy' as GoalHealth);
}

function aggregateObjectiveProgress(goals: EnrichedGoalNode[]): number {
  if (goals.length === 0) return 0;
  return Math.round(goals.reduce((sum, g) => sum + g.progress, 0) / goals.length);
}

export function buildBowlingChart(
  createdAt: string,
  targetDate: string | null,
  currentProgress: number,
  maxMonths = 8,
): BowlingMonth[] {
  const created = new Date(createdAt);
  const target = targetDate ? new Date(targetDate) : new Date();
  const now = new Date();
  const start = new Date(created.getFullYear(), created.getMonth(), 1);
  const end = new Date(target.getFullYear(), target.getMonth(), 1);
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  const cur = new Date(start);
  const slots: Date[] = [];
  while (cur <= end && slots.length < maxMonths) {
    slots.push(new Date(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  const monthCount = Math.max(1, slots.length);

  return slots.map((slot, index) => {
    const m = slot.getMonth();
    const y = slot.getFullYear();
    const monthKey = `${y}-${String(m + 1).padStart(2, '0')}`;
    const isPast = slot < new Date(now.getFullYear(), now.getMonth(), 1);
    const isCurrent = slot.getFullYear() === now.getFullYear() && slot.getMonth() === now.getMonth();
    const planned = Math.round(((index + 1) / monthCount) * 100);
    const actual = isPast || isCurrent
      ? Math.round((currentProgress / 100) * planned)
      : null;
    const gap = actual != null ? actual - planned : null;
    let tone: BowlingMonth['tone'] = 'neutral';
    if (gap != null) {
      if (gap >= 0) tone = 'green';
      else if (gap >= -5) tone = 'amber';
      else tone = 'red';
    }
    return {
      label: monthNames[m],
      monthKey,
      planned,
      actual,
      gap,
      tone,
      isPast,
      isCurrent,
    };
  });
}

function buildProjectSummary(
  project: EnterpriseProject,
  projectActions: LinkedAction[],
  projectIssues: ProjectHubIssue[],
): LinkedProjectSummary {
  const open = projectActions.filter((a) => a.status !== 'completed' && a.status !== 'cancelled');
  const overdue = projectActions.filter(isActionOverdue);
  const completed = projectActions.filter((a) => a.status === 'completed');
  const progress = projectActions.length > 0
    ? Math.round((completed.length / projectActions.length) * 100)
    : project.progress ?? 0;
  const criticalProblems = projectIssues.filter(isIssueCritical).length;
  const health = computeGoalHealth(
    progress,
    50,
    progress >= 100 ? 'completed' : overdue.length > 0 ? 'at_risk' : 'on_track',
    overdue.length,
    criticalProblems,
  );
  const economia = completed.length * 12000;
  const investimento = projectActions.length * 4500;
  const roiPercent = investimento > 0 ? Math.round(((economia - investimento) / investimento) * 100) : null;

  return {
    id: project.id,
    name: project.name,
    status: project.status,
    progress,
    health,
    roiPercent,
    economia,
    actionCount: projectActions.length,
    openActions: open.length,
    overdueActions: overdue.length,
    problemCount: projectIssues.length,
    criticalProblems,
  };
}

function enrichGoal(
  goal: TacticalGoalNode,
  objective: ObjectiveNode,
  actions: LinkedAction[],
  input: BuildHoshinDashboardInput,
  projectById: Map<string, EnterpriseProject>,
): EnrichedGoalNode {
  const deadline = goal.target_date ?? objective.target_date;
  const plannedProgress = computePlannedProgress(goal.created_at, deadline);
  const tacticalStatus = computeGoalStatus(goal.progress, goal.created_at, deadline);
  const linkedActions = actions.filter((a) => a.hoshin_goal_id === goal.id);
  const overdueActions = linkedActions.filter(isActionOverdue).length;
  const openActions = linkedActions.filter((a) => a.status !== 'completed' && a.status !== 'cancelled').length;

  const projectIds = [...new Set(linkedActions.map((a) => a.project_id).filter(Boolean))] as string[];
  const linkedProjects = projectIds.map((pid) => {
    const project = projectById.get(pid);
    if (!project) return null;
    const pActions = actions.filter((a) => a.project_id === pid);
    const pIssues = input.issuesByProject.get(pid) ?? [];
    return buildProjectSummary(project, pActions, pIssues);
  }).filter((p): p is LinkedProjectSummary => p != null);

  const linkedIndicators = indicatorsForProjects(projectIds, input.indicators);
  const linkedIssues = projectIds.flatMap((pid) => input.issuesByProject.get(pid) ?? []);
  const criticalIssues = linkedIssues.filter(isIssueCritical).length;

  const health = computeGoalHealth(goal.progress, plannedProgress, tacticalStatus, overdueActions, criticalIssues);
  const achievementProbability = computeAchievementProbability({
    actualProgress: goal.progress,
    plannedProgress,
    completedActions: goal.completed_action_count,
    totalActions: goal.action_count,
    overdueActions,
    criticalIssues,
    daysRemaining: daysRemaining(deadline),
    createdAt: goal.created_at,
    targetDate: deadline,
  });

  return {
    ...goal,
    health,
    plannedProgress,
    achievementProbability,
    probabilityLabel: probabilityLabel(achievementProbability),
    overdueActions,
    openActions,
    criticalIssues,
    linkedProjects,
    linkedIndicators,
    linkedActions,
    linkedIssues,
    bowling: buildBowlingChart(goal.created_at, deadline, goal.progress),
    ownerName: input.memberNameById.get(goal.assigned_to) ?? '—',
    departmentName: input.departmentNameById.get(goal.department_id) ?? '—',
  };
}

function indicatorsForProjects(projectIds: string[], indicators: Indicator[]): Indicator[] {
  const set = new Set(projectIds);
  return indicators.filter((ind) => ind.project_id && set.has(ind.project_id) && ind.status === 'active');
}

function enrichObjective(
  objective: ObjectiveNode,
  actions: LinkedAction[],
  input: BuildHoshinDashboardInput,
  projectById: Map<string, EnterpriseProject>,
): EnrichedObjectiveNode {
  const enrichedGoals = objective.goals.map((g) => enrichGoal(g, objective, actions, input, projectById));
  const actualProgress = objective.status === 'achieved'
    ? 100
    : objective.status === 'canceled'
      ? 0
      : aggregateObjectiveProgress(enrichedGoals);
  const plannedProgress = computePlannedProgress(objective.created_at, objective.target_date);

  const linkedActions = enrichedGoals.flatMap((g) => g.linkedActions);
  const linkedProjectsMap = new Map<string, LinkedProjectSummary>();
  for (const g of enrichedGoals) {
    for (const p of g.linkedProjects) linkedProjectsMap.set(p.id, p);
  }
  const linkedProjects = [...linkedProjectsMap.values()];
  const linkedIndicators = indicatorsForProjects(linkedProjects.map((p) => p.id), input.indicators);
  const linkedIssues = linkedProjects.flatMap((p) => input.issuesByProject.get(p.id) ?? []);
  const overdueActions = linkedActions.filter(isActionOverdue).length;
  const criticalIssues = linkedIssues.filter(isIssueCritical).length;

  const health = enrichedGoals.length > 0
    ? worstHealth(enrichedGoals.map((g) => g.health))
    : computeGoalHealth(actualProgress, plannedProgress, computeGoalStatus(actualProgress, objective.created_at, objective.target_date), overdueActions, criticalIssues);

  const achievementProbability = enrichedGoals.length > 0
    ? Math.round(enrichedGoals.reduce((s, g) => s + g.achievementProbability, 0) / enrichedGoals.length)
    : computeAchievementProbability({
      actualProgress,
      plannedProgress,
      completedActions: linkedActions.filter((a) => a.status === 'completed').length,
      totalActions: linkedActions.length,
      overdueActions,
      criticalIssues,
      daysRemaining: daysRemaining(objective.target_date),
      createdAt: objective.created_at,
      targetDate: objective.target_date,
    });

  return {
    ...objective,
    goals: enrichedGoals,
    health,
    actualProgress,
    plannedProgress,
    achievementProbability,
    probabilityLabel: probabilityLabel(achievementProbability),
    linkedProjects,
    linkedIndicators,
    linkedActions,
    linkedIssues,
    bowling: buildBowlingChart(objective.created_at, objective.target_date, actualProgress),
    sponsorName: input.memberNameById.get(objective.created_by) ?? '—',
    overdueActions,
    criticalIssues,
  };
}

export function buildHoshinDashboardData(input: BuildHoshinDashboardInput): HoshinDashboardData {
  const projectById = new Map(input.projects.map((p) => [p.id, p]));
  const indicatorById = new Map(input.indicators.map((i) => [i.id, i]));

  const objectives = input.tree.map((obj) => enrichObjective(obj, input.actions, input, projectById));

  const allGoals = objectives.flatMap((o) => o.goals);
  const summary: HoshinExecutiveSummary = {
    totalObjectives: objectives.filter((o) => o.status === 'active').length,
    healthyGoals: allGoals.filter((g) => g.health === 'healthy').length,
    attentionGoals: allGoals.filter((g) => g.health === 'attention').length,
    atRiskGoals: allGoals.filter((g) => g.health === 'at_risk').length,
    criticalGoals: allGoals.filter((g) => g.health === 'critical').length,
    averageProbability: allGoals.length > 0
      ? Math.round(allGoals.reduce((s, g) => s + g.achievementProbability, 0) / allGoals.length)
      : 0,
    linkedProjects: new Set(objectives.flatMap((o) => o.linkedProjects.map((p) => p.id))).size,
    overdueActions: objectives.reduce((s, o) => s + o.overdueActions, 0),
    criticalProblems: objectives.reduce((s, o) => s + o.criticalIssues, 0),
    totalActions: input.actions.filter((a) => a.hoshin_goal_id).length,
  };

  return { objectives, summary, projectById, indicatorById };
}

export function logHoshinDashboardCounts(data: HoshinDashboardData): void {
  console.log('[HoshinDashboard]', {
    Objetivos: data.objectives.length,
    'Metas Saudáveis': data.summary.healthyGoals,
    'Metas em Risco': data.summary.atRiskGoals + data.summary.criticalGoals,
    'Prob. Média': `${data.summary.averageProbability}%`,
    Projetos: data.summary.linkedProjects,
    'Ações Atrasadas': data.summary.overdueActions,
    'Problemas Críticos': data.summary.criticalProblems,
  });
}
